/**
 * Projects → Evidence sub-agent live-stream state.
 *
 * The Projects backend invokes one of two LLM-facing bindings
 * (``answer_with_evidence`` / ``lookup_evidence``) which both dispatch
 * to a single handler that runs the full Evidence chat loop server-side
 * (``stream_anthropic_with_evidence_tool``). Each SSE chunk that loop
 * yields is wrapped in an ``evidence.chunk`` envelope on the per-user
 * agent-event bus carrying ``parent_tool_call_id`` + ``subagent_run_id``
 * + monotonic ``seq`` + the untouched inner chunk.
 *
 * This module owns the FE half:
 *
 *   * ``evidenceSessionsAtom`` — a ``Map<parent_tool_call_id,
 *     SessionConversation>`` updated as envelopes arrive. The
 *     ``SessionConversation`` shape is the canonical Evidence chat
 *     reducer's output, so the same leaf components
 *     (``SearchBlock``, ``LibrarySearchBlock``,
 *     ``MedicalCalculator``, ``PdfExport``, …) render directly off
 *     these timelines without any forks.
 *
 *   * ``handleEvidenceChunkFrame`` — validates one ``evidence.chunk``
 *     full-envelope frame, ensures the parent tool_call slot exists,
 *     and folds the inner chunk into the existing reducer
 *     (``processChatChunk``). Mounted on ``dispatchRawFrame`` in
 *     ``AgentEventBus``.
 *
 * Re-using the Evidence reducer instead of forking a Projects-specific
 * one means UI parity is automatic: any future Evidence chunk type
 * gets rendered the same way in Projects without a code change here.
 */

import { logger } from '@/utils/logger/logger';
import type { LLMStreamEvent } from '@heidi/agents/contracts';
import type { RawAgentFrame } from '@heidi/agents/data';
import type {
  Chunk,
  SessionConversation,
  StreamingState,
  TimelineEntry,
} from '@heidi/evidence/contracts';
import { ChatMode } from '@heidi/evidence/contracts';
import { processChatChunk } from '@heidi/evidence/data';
import { atom, getDefaultStore } from 'jotai';
import { z } from 'zod';

// ── Wire schema ─────────────────────────────────────────────────────

/**
 * Evidence chunk envelope as it lands on the wire (per BE
 * ``EvidenceChunkEvent`` in ``agent_event_contracts.py``).
 *
 * ``inner`` is intentionally an opaque object — the Evidence chunk
 * schema lives in ``@/components/chat/types/types`` and evolves
 * independently of the harness. Validating it here would couple the
 * two surfaces; we hand it straight to the reducer instead and let
 * any rejection happen at that layer.
 */
const evidenceChunkEnvelopeSchema = z.object({
  type: z.literal('evidence.chunk'),
  schema_version: z.literal(1),
  event_id: z.string().min(1),
  agent_run_id: z.string(),
  turn_id: z.string().nullable().optional(),
  issued_at: z.string(),
  payload: z.object({
    parent_tool_call_id: z.string().min(1),
    subagent_run_id: z.string().min(1),
    seq: z.number().int().nonnegative(),
    inner: z.record(z.unknown()),
  }),
});

export type EvidenceChunkEnvelope = z.infer<typeof evidenceChunkEnvelopeSchema>;

export const EVIDENCE_CHUNK_EVENT_NAME = 'evidence.chunk';

// ── Per-call evidence session state ─────────────────────────────────

/**
 * Keyed by ``parent_tool_call_id``: each entry is the canonical
 * ``SessionConversation`` the Evidence reducer produces, scoped to one
 * ``answer_with_evidence`` / ``lookup_evidence`` invocation. The
 * Projects message renderer reads this map to find the timeline for a
 * given ``tool_call`` block and mounts the existing Evidence leaf
 * blocks against it.
 *
 * ``Map`` rather than a plain object: tool_call_ids look like
 * ``toolu_01ABC…`` and Jotai's structural sharing is the same either
 * way; ``Map`` gives us a clean ``get`` API on the hook side without
 * worrying about key collisions with object prototype properties.
 */
export const evidenceSessionsAtom = atom<ReadonlyMap<string, SessionConversation>>(
  new Map()
);

/**
 * Initial ``SessionConversation`` for a freshly-started sub-agent
 * call. The reducer requires a non-null ``streamingState`` to do
 * anything; without it ``processChatChunk`` early-returns ``null``.
 *
 * ``basePosition: 0`` is correct because each sub-agent run owns its
 * own timeline (we never interleave Projects parent messages into this
 * timeline — they live on the Projects message bubble itself).
 */
const createInitialSession = (): SessionConversation => {
  const now = Date.now();
  const streamingState: StreamingState = {
    basePosition: 0,
    activeIndices: new Map<number, number>(),
    currentActiveIndex: null,
    activityGroupOpen: false,
    streamStartedAt: now,
    lastChunkAt: now,
    lastVisibleContentAt: now,
  };
  const timeline: TimelineEntry[] = [];
  return {
    timeline,
    streamingState,
    metadata: { lastActivity: now },
  };
};

// ── Write-side: shared ingest + per-channel handlers ─────────────────

/**
 * Fold one Evidence inner chunk into the session keyed by its parent
 * tool_call. Shared by both ingest channels: the legacy agent-event-bus
 * frame path (``handleEvidenceChunkFrame``, still used by the desktop
 * overlay) and the per-run LLM stream path
 * (``handleEvidenceChunkStreamEvent``, used by Projects). Drops chunks
 * the reducer can't fold.
 */
// Evidence is folded by a single stream owner (the ProjectChat transport, not
// the background runner — see `useProjectRunStreamObserver`), so chunks arrive
// in the stream's native order. That single connection can still redeliver a
// chunk on reconnect, and the Evidence reducer appends (it is not idempotent),
// so dedupe by the per-sub-agent `seq` before folding — a *set* of folded seqs
// keyed by the parent tool_call (1:1 with the sub-agent run, matching the atom
// key). Bounded by the sub-agent run's chunk count.
const foldedSeqsByToolCall = new Map<string, Set<number>>();

/** Test-only: clear the per-tool-call evidence-chunk dedupe state. */
export const resetEvidenceChunkDedupeForTests = (): void => {
  foldedSeqsByToolCall.clear();
};

const ingestEvidenceChunk = (
  parentToolCallId: string,
  seq: number,
  inner: Chunk
): void => {
  let foldedSeqs = foldedSeqsByToolCall.get(parentToolCallId);
  if (!foldedSeqs) {
    foldedSeqs = new Set<number>();
    foldedSeqsByToolCall.set(parentToolCallId, foldedSeqs);
  }
  if (foldedSeqs.has(seq)) return;
  foldedSeqs.add(seq);

  const store = getDefaultStore();
  const current = store.get(evidenceSessionsAtom);
  const existing = current.get(parentToolCallId) ?? createInitialSession();

  // ChatMode.EVIDENCE matches the original surface so the reducer's
  // mode-dependent entry factories (e.g. text grouping rules) behave
  // identically inside Projects.
  const result = processChatChunk(inner, existing, ChatMode.EVIDENCE);
  if (!result) {
    // The reducer returned null — either the inner chunk doesn't carry
    // ``index`` (rare, e.g. ``message_info`` post-processing) or the
    // streamingState was somehow null. Drop without touching state.
    return;
  }

  const next: SessionConversation = {
    timeline: result.timeline,
    streamingState: result.streamingState,
    metadata: { ...existing.metadata, lastActivity: Date.now() },
  };
  // ``Map`` is mutable; clone so Jotai notices the reference change
  // (subscribers compare by ``Object.is``).
  const updated = new Map(current);
  updated.set(parentToolCallId, next);
  store.set(evidenceSessionsAtom, updated);
};

// The wire ``inner`` is a typed ``Chunk`` from the FE's perspective but
// the harness keeps it an opaque dict server-side (the chunk schema lives
// in ``@/components/chat/types/types`` and evolves independently). Cast
// through ``unknown`` so TypeScript treats the narrowing as the
// intentional trust-boundary crossing it is. ``processChatChunk``
// discriminates on ``chunk.type`` and early-returns for shapes it doesn't
// recognise, so an unknown chunk type degrades gracefully.
const asChunk = (inner: Record<string, unknown>): Chunk => inner as unknown as Chunk;

/**
 * Ingest one raw ``evidence.chunk`` SSE frame off the per-user agent-event
 * bus. Still wired by the desktop overlay (``useOverlayEvidenceStream``),
 * which runs its own bus subscription in a separate webview process. The
 * main-window Projects surface consumes evidence off the per-run LLM
 * stream instead — see ``handleEvidenceChunkStreamEvent``.
 *
 * Failures (malformed JSON, schema mismatch, reducer returning null for an
 * unknown chunk type) are logged and dropped — the bus is at-least-once
 * and we'd rather skip one bad envelope than poison the map for the rest
 * of the sub-agent run.
 */
export const handleEvidenceChunkFrame = (frame: RawAgentFrame): void => {
  if (frame.eventName !== EVIDENCE_CHUNK_EVENT_NAME) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(frame.data);
  } catch (error) {
    logger.error('[projects/evidence-chunk] Failed to parse frame', {
      error: error instanceof Error ? error.message : String(error),
    });
    return;
  }

  const validated = evidenceChunkEnvelopeSchema.safeParse(parsed);
  if (!validated.success) {
    logger.debug('[projects/evidence-chunk] Dropping malformed envelope', {
      issues: validated.error.issues,
    });
    return;
  }

  const { payload } = validated.data;
  ingestEvidenceChunk(payload.parent_tool_call_id, payload.seq, asChunk(payload.inner));
};

/**
 * Ingest one ``evidence.chunk`` event off the per-run LLM stream
 * (``GET …/:id/stream``). The Evidence sub-agent is a headless tool loop
 * inside the parent run, so its chunks ride the parent run's stream — the
 * same observer that drives computer-use child discovery routes them here
 * (see ``useProjectRunStreamObserver``). Mirrors ``handleEvidenceChunkFrame``
 * but reads the slim ``LLMStreamEvent`` ``evidence_chunk`` payload instead
 * of a full agent-event envelope. No-ops on any non-evidence event.
 */
export const handleEvidenceChunkStreamEvent = (event: LLMStreamEvent): void => {
  if (event.type !== EVIDENCE_CHUNK_EVENT_NAME) return;

  const chunk = event.evidence_chunk;
  if (!chunk?.parent_tool_call_id) {
    logger.debug('[projects/evidence-chunk] Dropping malformed stream event', {
      hasChunk: !!chunk,
    });
    return;
  }

  ingestEvidenceChunk(chunk.parent_tool_call_id, chunk.seq, asChunk(chunk.inner));
};
